import { useMemo } from 'react';

import { useDynamicCoverageRules } from '@/shared/hooks';
import { filterSmbRulesByLeaseDuration } from '@/shared/utils/smb-coverage-tiers.util';
import {
  buildDynamicSmbCoverageOptions,
  buildStaticSmbCoverageOptions,
  findSmbCoverageOption,
} from '../utils/coverage.util';

type UseSmbCoverageOptionsProps = {
  propertyId?: string;
  leaseStart?: Date;
  leaseEnd?: Date;
  grossMonthlyRent?: number;
  prepaidRent?: number;
  freeRent?: number;
  leaseDurationMonths: number;
  selectedValue?: number | string;
  selectedId?: string;
  enabled?: boolean;
};

export const useSmbCoverageOptions = ({
  propertyId,
  leaseStart,
  leaseEnd,
  grossMonthlyRent,
  prepaidRent,
  freeRent,
  leaseDurationMonths,
  selectedValue,
  selectedId,
  enabled = true,
}: UseSmbCoverageOptionsProps) => {
  const {
    data: coverageRulesFromApi,
    isLoading,
    isError,
    error,
  } = useDynamicCoverageRules({
    propertyId,
    leaseStart,
    leaseEnd,
    grossMonthlyRent,
    prepaidRent,
    freeRent,
    enabled,
    waitForPricingParams: true,
  });

  const dynamicCoverageOptions = useMemo(
    () =>
      buildDynamicSmbCoverageOptions(
        filterSmbRulesByLeaseDuration(coverageRulesFromApi ?? [], leaseStart, leaseEnd),
      ),
    [coverageRulesFromApi, leaseStart, leaseEnd],
  );

  const coverageOptions = useMemo(
    /**
     * When DDS omits pricing we intentionally preserve the legacy SMB radios.
     * That keeps the form usable in environments/properties where pricing is
     * unavailable. The FE calculations are not great, this should not happen ever.
     */
    () =>
      dynamicCoverageOptions.length > 0
        ? dynamicCoverageOptions
        : buildStaticSmbCoverageOptions({
            grossMonthlyRent,
            freeRent,
            prepaidRent,
            leaseDurationMonths,
          }),
    [dynamicCoverageOptions, freeRent, grossMonthlyRent, leaseDurationMonths, prepaidRent],
  );

  const selectedCoverageOption = useMemo(
    () => findSmbCoverageOption(coverageOptions, selectedValue, selectedId),
    [coverageOptions, selectedValue, selectedId],
  );

  return {
    coverageOptions,
    selectedCoverageOption,
    isUsingDynamicPricing: dynamicCoverageOptions.length > 0,
    isLoading,
    isError,
    error,
  };
};
